import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectTenantCurrency } from '../store/slices/tenantSlice';
import {
  chargeFieldLabel,
  formatCurrency,
  formatCurrencyCompact,
  formatCurrencyWhole,
  formatMoney,
  getCurrencySymbol,
  normalizeCurrencyCode,
  setActiveCurrency,
  type FormatMoneyArg,
} from '../utils/currency';

/** Tenant-scoped currency formatters for dashboard and admin UI. */
export function useTenantCurrency() {
  const currency = useSelector(selectTenantCurrency);
  const code = useMemo(() => normalizeCurrencyCode(currency), [currency]);

  useEffect(() => {
    setActiveCurrency(currency || code || '');
  }, [currency, code]);

  const symbol = useMemo(() => getCurrencySymbol(code || currency), [code, currency]);

  const formatAmount = useCallback(
    (amount: number, from?: unknown) => (
      from != null
        ? formatMoney(amount, { from, currency: code || currency })
        : formatMoney(amount, code || currency || undefined)
    ),
    [code, currency],
  );

  const formatWhole = useCallback(
    (amount: number) => formatCurrencyWhole(amount, code || undefined),
    [code],
  );

  const formatCompact = useCallback(
    (amount: number) => formatCurrencyCompact(amount, code || undefined),
    [code],
  );

  const formatIntl = useCallback(
    (amount: number) => formatCurrency(amount, code || undefined, {
      minimumFractionDigits: code === 'JPY' ? 0 : 2,
      maximumFractionDigits: code === 'JPY' ? 0 : 2,
    }),
    [code],
  );

  const fieldLabel = useCallback(
    (label: string) => chargeFieldLabel(label, code || currency || undefined),
    [code, currency],
  );

  const money = useCallback(
    (amount: unknown, currencyOrOpts?: FormatMoneyArg) => (
      currencyOrOpts != null
        ? formatMoney(amount, currencyOrOpts)
        : formatMoney(amount, code || currency || undefined)
    ),
    [code, currency],
  );

  return {
    currency: code || currency || '',
    symbol,
    formatAmount,
    formatWhole,
    formatCompact,
    formatIntl,
    fieldLabel,
    formatMoney: money,
  };
}
